import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 disabled:hover:bg-primary disabled:hover:text-primary-foreground',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:hover:bg-destructive disabled:hover:text-destructive-foreground',
        outline:
          'border border-muted-foreground/20 bg-card hover:bg-accent hover:text-accent-foreground disabled:hover:bg-card disabled:hover:text-current',
        // For buttons used as form-field triggers (asset/playlist/schedule pickers, etc.).
        // Matches Input/Select styling: rounded-md (6px), transparent bg, border-input,
        // darker-border on hover (no bg fill), gray-filled bg when disabled.
        // Horizontal padding is handled for you: the compoundVariants block below forces
        // `px-3` at every text size to match Input/Select's 12px. Callers must NOT pass
        // `px-3` themselves — it is already applied and only adds noise.
        trigger:
          'rounded-md border border-input bg-transparent dark:bg-card font-normal text-foreground hover:border-muted-foreground/70 aria-invalid:border-destructive aria-invalid:hover:border-destructive aria-invalid:focus-visible:ring-destructive disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 disabled:hover:border-input',
        secondary:
          'bg-primary/10 text-primary hover:bg-primary/20 disabled:hover:bg-primary/10 disabled:hover:text-primary',
        ghost:
          'hover:bg-accent hover:text-accent-foreground disabled:hover:bg-transparent disabled:hover:text-current',
        link: 'text-primary underline-offset-4 hover:underline disabled:hover:no-underline',
      },
      size: {
        // Text buttons — height scale: 24, 32, 36, 40, 48
        xs: 'h-6 px-2 text-xs [&_svg]:size-3',
        sm: 'h-8 px-3 text-xs [&_svg]:size-3.5',
        md: 'h-9 px-4 py-2 [&_svg]:size-4',
        lg: 'h-10 px-4 text-base [&_svg]:size-4',
        xl: 'h-12 px-8 text-base [&_svg]:size-5',
        // Icon-only buttons — square at each height tier
        'icon-xs': 'h-6 w-6 [&_svg]:size-3',
        'icon-sm': 'h-8 w-8 [&_svg]:size-4',
        'icon-md': 'h-9 w-9 [&_svg]:size-4',
        'icon-lg': 'h-10 w-10 [&_svg]:size-5',
        'icon-xl': 'h-12 w-12 [&_svg]:size-5',
      },
    },
    compoundVariants: [
      // Trigger buttons act as form-field surrogates (asset/playlist/schedule pickers,
      // timezone pickers, etc.) — force 12px horizontal padding at any size so their
      // text aligns with adjacent Input/SelectTrigger (both px-3). Without this, cva
      // composes size AFTER variant, so size's px-4 (md/lg) or px-8 (xl) wins
      // tailwind-merge and pickers render farther-right than sibling fields.
      {
        variant: 'trigger',
        size: ['xs', 'sm', 'md', 'lg', 'xl'],
        className: 'px-3',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Tooltip shown when the button is disabled. Explains why the action is unavailable. */
  disabledTooltip?: string;
}

/** Sizing classes that must be copied onto the disabled-tooltip wrapper — see below. */
const WRAPPER_SIZING_CLASSES = ['w-full', 'flex-1', 'grow', 'flex-grow', 'min-w-0'] as const;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, disabledTooltip, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const button = (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={disabled && disabledTooltip ? undefined : ref}
        disabled={disabled}
        {...props}
      />
    );

    if (disabled && disabledTooltip) {
      // The tooltip trigger wraps the button in a shrink-to-fit <span>, so a button sized by its
      // PARENT (`w-full` sidebar buttons, `flex-1` toolbar buttons) would visibly shrink the moment
      // it became disabled. Mirror those sizing classes onto the wrapper so the layout is identical
      // whether or not the tooltip is present.
      const wrapperSizing = WRAPPER_SIZING_CLASSES.filter((c) =>
        className?.split(/\s+/).includes(c),
      );
      return (
        <TooltipPrimitive.Provider delayDuration={300}>
          <TooltipPrimitive.Root>
            <TooltipPrimitive.Trigger asChild>
              <span
                ref={ref as React.Ref<HTMLSpanElement>}
                tabIndex={0}
                className={cn('inline-flex cursor-not-allowed', wrapperSizing)}
              >
                {button}
              </span>
            </TooltipPrimitive.Trigger>
            <TooltipPrimitive.Portal>
              <TooltipPrimitive.Content
                sideOffset={4}
                // z-[80] matches the canonical Tooltip component so it sits
                // above Sheet (z-[51]) and AlertDialog (z-[70]) — otherwise a
                // disabled-button tooltip hides behind a confirm dialog.
                // `max-w-xs` (20rem / 320px) keeps long disabledTooltip strings
                // from stretching into a single long line — they wrap instead.
                className="z-[80] max-w-xs overflow-hidden rounded-md bg-tooltip px-3 py-1.5 text-xs text-tooltip-foreground animate-in fade-in-0 zoom-in-95"
              >
                {disabledTooltip}
              </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
          </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
      );
    }

    // Disabled button: wrap in a span so the not-allowed cursor still shows.
    // The inner button has pointer-events:none (via disabled:pointer-events-none)
    // which blocks hover CSS — but that also suppresses the button's own cursor.
    // The span catches the cursor and displays cursor-not-allowed.
    // If the button is full-width (w-full / flex-1), the wrapper must match —
    // otherwise an inline-flex span shrinks to content width and the disabled
    // state collapses while the idle state spans normally.
    if (disabled) {
      const isFullWidth =
        typeof className === 'string' &&
        (className.includes('w-full') || className.includes('flex-1'));
      return (
        <span className={cn('cursor-not-allowed', isFullWidth ? 'flex w-full' : 'inline-flex')}>
          {button}
        </span>
      );
    }

    return button;
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
